<script setup lang="ts">
import BaseIcon from '@/components/BaseIcon.vue'

defineProps<{
  id: number
  type: string
  title: string
  message: string
}>()

const emit = defineEmits<{
  close: [id: number]
}>()

</script>

<template>
  <div class="toast" :class="`toast-${type}`">
    <span class="toast-icon">
      <BaseIcon v-if="type === 'success'" name="check-circle" :size="20" />
      <BaseIcon v-else-if="type === 'error'" name="x-circle" :size="20" />
      <BaseIcon v-else-if="type === 'warning'" name="warning" :size="20" />
      <BaseIcon v-else name="info" :size="20" />
    </span>
    <div class="toast-content">
      <div v-if="title" class="toast-title">{{ title }}</div>
      <div class="toast-message">{{ message }}</div>
    </div>
    <button class="toast-close" type="button" @click="emit('close', id)">
      <BaseIcon name="x" :size="16" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.toast {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-3) var(--spacing-5);
  background: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-toast);
  min-width: 280px;
  max-width: 400px;
  pointer-events: auto;
  animation: toast-slide-in var(--transition);
  border-left: 4px solid var(--border-hover);

  &.toast-success {
    border-left-color: var(--success-text);
  }

  &.toast-error {
    border-left-color: var(--danger);
  }

  &.toast-warning {
    border-left-color: var(--warning);
  }

  &.toast-info {
    border-left-color: var(--primary);
  }
}

.toast-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(svg) {
    width: 20px;
    height: 20px;
  }
}

.toast-success .toast-icon {
  color: var(--success-text);
}

.toast-error .toast-icon {
  color: var(--danger);
}

.toast-warning .toast-icon {
  color: var(--warning);
}

.toast-info .toast-icon {
  color: var(--primary);
}

.toast-content {
  flex: 1;
}

.toast-title {
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-0-5);
}

.toast-message {
  font-size: var(--text-13);
  color: var(--text-secondary);
  line-height: 1.4;
}

.toast-close {
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius);
  transition: all var(--transition-fast);
  flex-shrink: 0;

  &:hover {
    background: var(--surface-dim);
    color: var(--text-secondary);
  }
}

@keyframes toast-slide-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
